import { QueueSearchSetting } from '@creative-kit/shared';
import type { RootStore } from 'logic';
import { action, computed, makeObservable, observable } from 'mobx';
import { matchesAnyNormalizedSearchTerm, splitNormalizedSearchTerms } from 'utils/textSearch';

export class UiCardSearchStore {
  currentSearch?: string;
  isSettingsMenuOpen = false;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      currentSearch: observable,
      setCurrentSearch: action.bound,
      searchTerms: computed,
      hasSearch: computed,
      searchMatchingRowsByModuleId: computed,
      isSettingsMenuOpen: observable,
      openSettingsMenu: action.bound,
      closeSettingsMenu: action.bound,
    });

    this.rootStore = rootStore;
  }

  // comma separated searches break out into multiple terms to search for
  get searchTerms() {
    return splitNormalizedSearchTerms(this.currentSearch);
  }

  get hasSearch() {
    return !!this.currentSearch?.trim();
  }

  // Find all rows across all sources that match the current search terms
  // This mimics the rowSelectionsByModuleId pattern used for filtering
  get searchMatchingRowsByModuleId(): Record<string, Set<string>> {
    if (!this.hasSearch || !this.rootStore.sessionsStore.currentSession) {
      return {};
    }

    const { currentSession } = this.rootStore.sessionsStore;
    const { sources } = this.rootStore.sourcesStore;
    const matchingRowsByModuleId: Record<string, Set<string>> = {};

    // Search through each module in the session
    currentSession.modules.forEach((module) => {
      const moduleSource = sources[module.sourceId];
      if (!moduleSource) return;

      // Determine which fields to search based on settings
      const activeFields =
        this.setting.type === 'all'
          ? moduleSource.ownFields.map((f) => f.fieldId)
          : this.setting.type === 'visible'
          ? module.fields
          : this.setting.type === 'module' && module.id === this.setting.moduleId
          ? this.setting.fieldIds
          : [];

      if (activeFields.length === 0) return;

      const cleanSearchTerms = this.searchTerms;
      const matchingRows = new Set<string>();

      // Search through all rows in this source
      Object.values(moduleSource.rows).forEach((row) => {
        const rowMatches = cleanSearchTerms.some((searchTerm) =>
          Object.entries(row.getFieldValues()).some(([fieldId, value]) => {
            return (
              activeFields.includes(fieldId) &&
              typeof value === 'string' &&
              value.length > 0 &&
              matchesAnyNormalizedSearchTerm(value, [searchTerm])
            );
          }),
        );

        if (rowMatches) {
          matchingRows.add(row.id);
        }
      });

      if (matchingRows.size > 0) {
        matchingRowsByModuleId[module.id] = matchingRows;
      }
    });

    return matchingRowsByModuleId;
  }

  setCurrentSearch(newSearch?: string) {
    this.currentSearch = newSearch;
  }

  openSettingsMenu() {
    this.isSettingsMenuOpen = true;
  }

  closeSettingsMenu() {
    this.isSettingsMenuOpen = false;
  }

  get setting(): QueueSearchSetting {
    return (
      this.rootStore.userSettingsStore.userSettings.queueSearch || {
        type: 'all',
        messages: false,
      }
    );
  }

  changeSearchSettingType = (type: string) => {
    if (type === 'all' || type === 'visible') {
      this.updateSearchSetting({
        type,
        messages: false,
      });
    }
  };

  changeSearchSettingMessages = (enabled: boolean) => {
    if (this.setting.type === 'all') {
      this.updateSearchSetting({
        ...this.setting,
        messages: enabled,
      });
    }
  };

  changeSearchSettingModule = (moduleId: string) => {
    // Get all field IDs for the selected module to turn them all on by default
    const { currentSession } = this.rootStore.sessionsStore;
    let allFieldIds: string[] = [];

    if (currentSession) {
      const module = currentSession.modules.find((m) => m.id === moduleId);
      if (module) {
        const source = this.rootStore.sourcesStore.sources[module.sourceId];
        if (source) {
          allFieldIds = source.ownFields.map((field) => field.fieldId);
        }
      }
    }

    this.updateSearchSetting({
      type: 'module',
      moduleId,
      fieldIds: allFieldIds,
    });
  };

  toggleSearchSettingModuleField = (fieldId: string) => {
    if (this.setting.type === 'module') {
      if (this.setting.fieldIds.includes(fieldId)) {
        this.updateSearchSetting({
          ...this.setting,
          fieldIds: this.setting.fieldIds.filter((f) => f !== fieldId),
        });
      } else {
        this.updateSearchSetting({
          ...this.setting,
          fieldIds: this.setting.fieldIds.concat(fieldId),
        });
      }
    }
  };

  private updateSearchSetting(newSetting: QueueSearchSetting) {
    this.rootStore.userSettingsStore.modifyUserSettings({
      queueSearch: newSetting,
    });
  }
}
